export interface MarketplacePriceRule {
  marketplace: string
  minimumPrice: number
  maximumPrice: number
  currency: string
  displayName: string
}

export interface PriceValidationResult {
  valid: boolean
  error?: string
  warning?: string
}

export interface PriceValidationError {
  field: string
  value: number
  marketplace: string
  minimum_required: number
  message: string
}

export class PriceValidationUtils {
  private static readonly GENERAL_MIN_PRICE = 0.01
  private static readonly GENERAL_MAX_PRICE = 99999999
  private static readonly MAX_DECIMAL_PLACES = 2

  private static readonly MARKETPLACE_RULES: Record<string, MarketplacePriceRule> = {
    shopee: {
      marketplace: 'shopee',
      minimumPrice: 1.00,
      maximumPrice: 99999999,
      currency: 'IDR',
      displayName: 'Shopee'
    },
    lazada: {
      marketplace: 'lazada',
      minimumPrice: 0.50,
      maximumPrice: 99999999,
      currency: 'IDR',
      displayName: 'Lazada'
    },
    tiktok: {
      marketplace: 'tiktok',
      minimumPrice: 1.00,
      maximumPrice: 99999999,
      currency: 'IDR',
      displayName: 'TikTok Shop'
    }
  }

  static getMarketplaceRules(): Record<string, MarketplacePriceRule> {
    return { ...this.MARKETPLACE_RULES }
  }

  static getMarketplaceRule(marketplace: string): MarketplacePriceRule | null {
    const normalizedMarketplace = marketplace.toLowerCase()
    return this.MARKETPLACE_RULES[normalizedMarketplace] || null
  }

  static validateGeneralPrice(price: number): PriceValidationResult {
    if (isNaN(price) || !isFinite(price)) {
      return {
        valid: false,
        error: 'Price must be a valid number'
      }
    }

    if (price < 0) {
      return {
        valid: false,
        error: 'Price cannot be negative'
      }
    }

    if (price < this.GENERAL_MIN_PRICE) {
      return {
        valid: false,
        error: `Price must be at least ${this.GENERAL_MIN_PRICE}`
      }
    }

    if (price > this.GENERAL_MAX_PRICE) {
      return {
        valid: false,
        error: `Price cannot exceed ${this.GENERAL_MAX_PRICE.toLocaleString()}`
      }
    }

    // Check decimal places
    const decimalPlaces = this.getDecimalPlaces(price)
    if (decimalPlaces > this.MAX_DECIMAL_PLACES) {
      return {
        valid: false,
        error: `Price can have maximum ${this.MAX_DECIMAL_PLACES} decimal places`
      }
    }

    return { valid: true }
  }

  static validateMarketplacePrice(marketplace: string, price: number): PriceValidationResult {
    // First validate general price rules
    const generalValidation = this.validateGeneralPrice(price)
    if (!generalValidation.valid) {
      return generalValidation
    }

    // Get marketplace-specific rules
    const rule = this.getMarketplaceRule(marketplace)
    if (!rule) {
      return {
        valid: false,
        error: `Unknown marketplace: ${marketplace}`
      }
    }

    // Check marketplace minimum
    if (price < rule.minimumPrice) {
      return {
        valid: false,
        error: `Price below ${rule.displayName} minimum: requires at least ${this.formatPrice(rule.minimumPrice, rule.currency)}`
      }
    }

    // Check marketplace maximum
    if (price > rule.maximumPrice) {
      return {
        valid: false,
        error: `Price exceeds ${rule.displayName} maximum: cannot exceed ${this.formatPrice(rule.maximumPrice, rule.currency)}`
      }
    }

    return { valid: true }
  }

  static validateBatchPrices(
    prices: Array<{ id: string; price: number; marketplace: string }>,
    allowEmpty: boolean = true
  ): { valid: boolean; errors: Record<string, string>; warnings: Record<string, string> } {
    const errors: Record<string, string> = {}
    const warnings: Record<string, string> = {}
    
    let hasValidPrices = false

    prices.forEach(({ id, price, marketplace }) => {
      // Skip validation for zero/empty prices if allowed
      if (allowEmpty && (price === 0 || isNaN(price))) {
        warnings[id] = 'Price not set - will not be updated'
        return
      }

      hasValidPrices = true
      const validation = this.validateMarketplacePrice(marketplace, price)
      
      if (!validation.valid) {
        errors[id] = validation.error || 'Invalid price'
      } else if (validation.warning) {
        warnings[id] = validation.warning
      }
    })

    // If no valid prices provided, add general error
    if (!hasValidPrices && allowEmpty) {
      errors['_general'] = 'At least one valid price must be provided'
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors,
      warnings
    }
  }

  static formatPrice(price: number, currency: string = 'IDR'): string {
    const formatter = new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: currency === 'IDR' ? 0 : 2,
      maximumFractionDigits: currency === 'IDR' ? 0 : 2,
    })

    try {
      return formatter.format(price)
    } catch (error) {
      // Fallback for unsupported currencies
      const symbol = this.getCurrencySymbol(currency)
      const formattedNumber = price.toLocaleString('id-ID', {
        minimumFractionDigits: currency === 'IDR' ? 0 : 2,
        maximumFractionDigits: currency === 'IDR' ? 0 : 2,
      })
      return `${symbol} ${formattedNumber}`
    }
  }

  static getCurrencySymbol(currency: string): string {
    const symbols: Record<string, string> = {
      IDR: 'Rp',
      USD: '$',
      EUR: '€',
      SGD: 'S$',
      MYR: 'RM',
      THB: '฿'
    }
    
    return symbols[currency.toUpperCase()] || currency
  }

  static getMarketplaceDisplayInfo(marketplace: string): { name: string; currency: string; minPrice: string } {
    const rule = this.getMarketplaceRule(marketplace)
    
    if (!rule) {
      return {
        name: marketplace,
        currency: 'IDR',
        minPrice: this.formatPrice(this.GENERAL_MIN_PRICE)
      }
    }

    return {
      name: rule.displayName,
      currency: rule.currency,
      minPrice: this.formatPrice(rule.minimumPrice, rule.currency)
    }
  }

  static generatePriceValidationError(
    field: string,
    value: number,
    marketplace: string
  ): PriceValidationError {
    const rule = this.getMarketplaceRule(marketplace)
    const minimumRequired = rule?.minimumPrice || this.GENERAL_MIN_PRICE
    
    return {
      field,
      value,
      marketplace,
      minimum_required: minimumRequired,
      message: `Price below marketplace minimum: ${marketplace} requires minimum ${minimumRequired}`
    }
  }

  private static getDecimalPlaces(num: number): number {
    const decimalStr = num.toString()
    if (decimalStr.indexOf('.') === -1) {
      return 0
    }
    return decimalStr.split('.')[1].length
  }

  static sanitizePrice(input: string): number {
    // Remove currency symbols and formatting
    const cleaned = input
      .replace(/[Rp$€S฿RM,\s]/g, '') // Remove common currency symbols and separators
      .replace(/[^\d.-]/g, '') // Keep only digits, dots, and minus
      .trim()

    if (cleaned === '' || cleaned === '-') {
      return 0
    }

    const parsed = parseFloat(cleaned)
    return isNaN(parsed) ? 0 : parsed
  }

  static formatPriceInput(value: string, marketplace?: string): string {
    const sanitized = this.sanitizePrice(value)
    if (sanitized === 0) return ''
    
    const rule = marketplace ? this.getMarketplaceRule(marketplace) : null
    const currency = rule?.currency || 'IDR'
    
    // For IDR, don't show decimals; for others, show up to 2 decimal places
    if (currency === 'IDR') {
      return Math.round(sanitized).toLocaleString('id-ID')
    }
    
    return sanitized.toLocaleString('id-ID', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })
  }
}

export default PriceValidationUtils